/**
 * Office hours processing utilities
 * Handles the complex logic for office hours with lunch breaks
 *
 * Key principles for TC-OH-006 fix:
 * 1. Idempotent normalization - running normalize twice yields same result
 * 2. Source preservation - always reconstruct from canonical office hours, not derived UI state
 * 3. Non-destructive editing - changing non-time fields never alters hours/lunch
 * 4. Create/Edit parity - both flows use identical normalization pipeline
 */

import { OfficeHour } from '../types/practices';
import { OfficeHoursInput } from '../types/practices/types';
import {
  isValid24hTime,
  minutesToTime,
  timeToMinutes,
  to12h,
} from './timeConversion';

export interface ProcessedOfficeHours {
  dayOfWeek: number;
  open_time: string;
  close_time: string;
  closed: boolean;
  endOfDay?: boolean | null;
}

/**
 * Canonical office hours representation using minutes since midnight
 * This is the single source of truth for all time calculations
 */
export interface CanonicalOfficeHours {
  dayOfWeek: number; // 1-7 (Sunday=1)
  blocks: Array<{
    startMinutes: number; // 0-1439 minutes since midnight
    endMinutes: number; // 0-1439 minutes since midnight
    id?: string; // For tracking existing records during updates
  }>;
  closed: boolean;
}

/**
 * Lobby closure hours configuration
 */
export interface LunchHoursConfig {
  startMinutes: number; // 0-1439 minutes since midnight
  endMinutes: number; // 0-1439 minutes since midnight
  enabled: boolean;
}

/**
 * Convert OfficeHour[] from API to canonical representation
 * This is the single entry point for all office hours data from the API
 */
export const toCanonical = (
  apiOfficeHours: OfficeHour[]
): CanonicalOfficeHours[] => {
  const result: CanonicalOfficeHours[] = [];

  // Group by day
  const byDay = new Map<number, OfficeHour[]>();
  apiOfficeHours.forEach((oh) => {
    if (!byDay.has(oh.day_of_week)) {
      byDay.set(oh.day_of_week, []);
    }
    byDay.get(oh.day_of_week)!.push(oh);
  });

  // Create canonical representation for each day (1-7)
  for (let dayOfWeek = 1; dayOfWeek <= 7; dayOfWeek++) {
    const dayHours = byDay.get(dayOfWeek) || [];

    if (dayHours.length === 0 || dayHours.every((oh) => oh.closed)) {
      result.push({
        dayOfWeek,
        blocks: [],
        closed: true,
      });
      continue;
    }

    // Filter non-closed hours and convert to minute blocks
    const blocks = dayHours
      .filter((oh) => !oh.closed && oh.open_time && oh.close_time)
      .map((oh) => ({
        startMinutes: timeToMinutes(oh.open_time!),
        endMinutes: timeToMinutes(oh.close_time!),
        id: oh.id,
      }))
      .filter((block) => block.startMinutes < block.endMinutes) // Valid time ranges only
      .sort((a, b) => a.startMinutes - b.startMinutes); // Sort by start time

    result.push({
      dayOfWeek,
      blocks,
      closed: blocks.length === 0,
    });
  }

  return result;
};

/**
 * Normalize canonical office hours by merging overlapping/touching blocks
 * This ensures consistent representation regardless of input source
 */
export const normalizeCanonical = (
  canonical: CanonicalOfficeHours[]
): CanonicalOfficeHours[] => {
  return canonical.map((day) => {
    if (day.closed || day.blocks.length === 0) {
      return { ...day, blocks: [], closed: true };
    }

    // Sort blocks by start time
    const sorted = [...day.blocks].sort(
      (a, b) => a.startMinutes - b.startMinutes
    );
    const merged: typeof sorted = [];

    for (const block of sorted) {
      if (merged.length === 0) {
        merged.push({ ...block });
        continue;
      }

      const last = merged[merged.length - 1];

      // Merge if blocks touch or overlap
      if (block.startMinutes <= last.endMinutes) {
        last.endMinutes = Math.max(last.endMinutes, block.endMinutes);
        // Keep the first ID for tracking
      } else {
        merged.push({ ...block });
      }
    }

    return {
      ...day,
      blocks: merged,
      closed: merged.length === 0,
    };
  });
};

/**
 * Apply lobby closure exclusion to canonical office hours
 * Returns view model for UI (office hours minus lunch break)
 * Does NOT mutate the source canonical hours
 */
export const applyLunchExclusion = (
  canonical: CanonicalOfficeHours[],
  lunchConfig: LunchHoursConfig | null
): CanonicalOfficeHours[] => {
  if (!lunchConfig?.enabled) {
    return canonical; // Return as-is if lunch not enabled
  }

  return canonical.map((day) => {
    if (day.closed || day.blocks.length === 0) {
      return day; // Skip closed days
    }

    // Check if any block spans the lunch period
    const newBlocks: typeof day.blocks = [];

    for (const block of day.blocks) {
      // If lunch period doesn't intersect this block, keep as-is
      if (
        block.endMinutes <= lunchConfig.startMinutes ||
        block.startMinutes >= lunchConfig.endMinutes
      ) {
        newBlocks.push(block);
        continue;
      }

      // If lunch period is completely within this block, split it
      if (
        block.startMinutes < lunchConfig.startMinutes &&
        block.endMinutes > lunchConfig.endMinutes
      ) {
        // Morning block: original start to lunch start
        newBlocks.push({
          startMinutes: block.startMinutes,
          endMinutes: lunchConfig.startMinutes,
          id: block.id,
        });

        // Afternoon block: lunch end to original end
        newBlocks.push({
          startMinutes: lunchConfig.endMinutes,
          endMinutes: block.endMinutes,
          // No ID for afternoon block as it's derived
        });
        continue;
      }

      // If lunch overlaps start of block, keep only the part after lunch
      if (
        block.startMinutes < lunchConfig.endMinutes &&
        block.endMinutes > lunchConfig.endMinutes
      ) {
        newBlocks.push({
          startMinutes: lunchConfig.endMinutes,
          endMinutes: block.endMinutes,
          id: block.id,
        });
        continue;
      }

      // If lunch overlaps end of block, keep only the part before lunch
      if (
        block.startMinutes < lunchConfig.startMinutes &&
        block.endMinutes > lunchConfig.startMinutes
      ) {
        newBlocks.push({
          startMinutes: block.startMinutes,
          endMinutes: lunchConfig.startMinutes,
          id: block.id,
        });
        continue;
      }

      // If lunch completely covers this block, skip it
      // (This case is handled by the continue statements above)
    }

    return {
      ...day,
      blocks: newBlocks,
      closed: newBlocks.length === 0,
    };
  });
};

/**
 * Convert canonical back to OfficeHoursInput[] for form display
 * This is the single exit point for UI forms
 */
export const fromCanonical = (
  canonical: CanonicalOfficeHours[]
): OfficeHoursInput[] => {
  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  return dayNames.map((dayName, index) => {
    const dayData = canonical.find((c) => c.dayOfWeek === index + 1);

    if (!dayData || dayData.closed || dayData.blocks.length === 0) {
      return {
        day: dayName,
        open: '',
        close: '',
        closed: true,
        openDisplay: '',
        closeDisplay: '',
      };
    }

    // For UI display, show the span from earliest start to latest end
    const earliestStart = Math.min(
      ...dayData.blocks.map((b) => b.startMinutes)
    );
    const latestEnd = Math.max(
      ...dayData.blocks.map((b) => b.endMinutes)
    );

    const openTime = minutesToTime(earliestStart);
    const closeTime = minutesToTime(latestEnd);

    return {
      day: dayName,
      open: openTime,
      close: closeTime,
      closed: false,
      openDisplay: to12h(openTime),
      closeDisplay: to12h(closeTime),
    };
  });
};

/**
 * Processes office hours input to create the appropriate API payload
 * Handles lobby closure hours splitting and validation
 */
export const processOfficeHoursForAPI = (
  officeHoursInput: OfficeHoursInput[],
  lunchHours: { start: string; end: string } | null
): ProcessedOfficeHours[] => {
  const result: ProcessedOfficeHours[] = [];

  officeHoursInput.forEach((day, index) => {
    const dayOfWeek = index + 1; // Convert 0-based index to 1-based day of week (Sunday=1, Monday=2, etc.)

    // Skip closed days or days without valid times
    if (
      day.closed ||
      !day.open ||
      !day.close ||
      !isValid24hTime(day.open) ||
      !isValid24hTime(day.close)
    ) {
      return;
    }

    // Validate that open time is before close time
    const openMinutes = timeToMinutes(day.open);
    const closeMinutes = timeToMinutes(day.close);

    if (openMinutes >= closeMinutes) {
      console.warn(
        `Invalid office hours for ${day.day}: open time must be before close time`
      );
      return;
    }

    // Check if lobby closure hours should be applied to this day
    // Lobby closure can only be applied if office hours span the entire closure period
    // This means: office opens before lobby closure starts AND lobby closure ends before office closes
    const shouldApplyLunch =
      lunchHours &&
      lunchHours.start &&
      lunchHours.end &&
      isValid24hTime(lunchHours.start) &&
      isValid24hTime(lunchHours.end) &&
      timeToMinutes(lunchHours.start) <
        timeToMinutes(lunchHours.end) &&
      timeToMinutes(day.open) < timeToMinutes(lunchHours.start) &&
      timeToMinutes(lunchHours.end) < timeToMinutes(day.close);

    if (shouldApplyLunch) {
      // Split day into two segments: morning and afternoon
      result.push({
        dayOfWeek,
        open_time: day.open,
        close_time: lunchHours.start,
        closed: false,
        endOfDay: false,
      });

      result.push({
        dayOfWeek,
        open_time: lunchHours.end,
        close_time: day.close,
        closed: false,
        endOfDay: true,
      });
    } else {
      // Single time segment for the day
      result.push({
        dayOfWeek,
        open_time: day.open,
        close_time: day.close,
        closed: false,
        endOfDay: null,
      });
    }
  });

  return result;
};

/**
 * Maps office hours from API response to form input format
 * Handles lobby closure hours reconstruction and time conversion
 */
export const mapOfficeHoursToForm = (
  officeHoursData: OfficeHour[]
): OfficeHoursInput[] => {
  const officeHours = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  return officeHours.map((dayName, index) => {
    const dayOfWeek = index + 1;
    const dayOfficeHours = officeHoursData.filter(
      (oh) => oh.day_of_week === dayOfWeek
    );

    // If no hours or all closed, return closed day
    if (
      !dayOfficeHours.length ||
      dayOfficeHours.every((oh) => oh.closed)
    ) {
      return {
        day: dayName,
        open: '',
        close: '',
        closed: true,
        openDisplay: '',
        closeDisplay: '',
      };
    }

    // Filter out closed hours
    const nonClosed = dayOfficeHours.filter((oh) => !oh.closed);

    if (nonClosed.length === 0) {
      return {
        day: dayName,
        open: '',
        close: '',
        closed: true,
        openDisplay: '',
        closeDisplay: '',
      };
    }

    // Sort by open time to get earliest start - use proper time comparison
    const sortedByOpen = [...nonClosed].sort((a, b) => {
      const aOpen = a.open_time || '';
      const bOpen = b.open_time || '';

      // Convert to minutes for proper time comparison instead of string comparison
      const aMinutes = timeToMinutes(aOpen);
      const bMinutes = timeToMinutes(bOpen);

      return aMinutes - bMinutes;
    });

    const earliestOpen = sortedByOpen[0]?.open_time;

    // Find latest close time
    const latestClose = nonClosed.reduce((latest, curr) => {
      const latestCloseTime = latest?.close_time || '';
      const currCloseTime = curr?.close_time || '';

      if (!latestCloseTime) return curr;
      if (!currCloseTime) return latest;

      const latestMinutes = timeToMinutes(latestCloseTime);
      const currMinutes = timeToMinutes(currCloseTime);

      return currMinutes > latestMinutes ? curr : latest;
    }, nonClosed[0]);

    const latestCloseTime = latestClose?.close_time;

    // Convert to 12-hour format for display
    const openDisplay = earliestOpen ? to12h(earliestOpen) : '';
    const closeDisplay = latestCloseTime
      ? to12h(latestCloseTime)
      : '';

    return {
      day: dayName,
      open: earliestOpen || '',
      close: latestCloseTime || '',
      closed: false,
      openDisplay,
      closeDisplay,
    };
  });
};

/**
 * Extracts lobby closure hours from office hours data
 * Looks for days with two segments (morning and afternoon)
 */
export const extractLunchHours = (
  officeHoursData: OfficeHour[]
): { start: string; end: string } | null => {
  const byDay = new Map<number, OfficeHour[]>();

  // Group by day of week
  officeHoursData.forEach((oh) => {
    if (!oh.closed) {
      const arr = byDay.get(oh.day_of_week) || [];
      arr.push(oh);
      byDay.set(oh.day_of_week, arr);
    }
  });

  // Look for days with exactly 2 segments (indicating lobby closure hours)
  for (const [, dayHours] of byDay) {
    if (dayHours.length === 2) {
      // Sort by open time - use proper time comparison
      const sorted = [...dayHours].sort((a, b) => {
        const aTime = a.open_time || '';
        const bTime = b.open_time || '';

        // Convert to minutes for proper time comparison instead of string comparison
        const aMinutes = timeToMinutes(aTime);
        const bMinutes = timeToMinutes(bTime);

        return aMinutes - bMinutes;
      });

      const firstSegment = sorted[0];
      const secondSegment = sorted[1];

      if (firstSegment.close_time && secondSegment.open_time) {
        return {
          start: firstSegment.close_time,
          end: secondSegment.open_time,
        };
      }
    }
  }

  return null;
};

/**
 * Validates office hours input
 * Returns array of error messages
 */
export const validateOfficeHours = (
  officeHoursInput: OfficeHoursInput[],
  lunchHours: { start: string; end: string } | null
): string[] => {
  const errors: string[] = [];

  // Validate each day
  officeHoursInput.forEach((day) => {
    if (day.closed) return;

    if (!day.open || !day.close) {
      errors.push(
        `${day.day}: Both open and close times are required`
      );
      return;
    }

    if (!isValid24hTime(day.open) || !isValid24hTime(day.close)) {
      errors.push(`${day.day}: Invalid time format`);
      return;
    }

    const openMinutes = timeToMinutes(day.open);
    const closeMinutes = timeToMinutes(day.close);

    if (openMinutes >= closeMinutes) {
      errors.push(`${day.day}: Open time must be before close time`);
    }
  });

  // Validate lunch hours if enabled
  if (lunchHours && lunchHours.start && lunchHours.end) {
    if (
      !isValid24hTime(lunchHours.start) ||
      !isValid24hTime(lunchHours.end)
    ) {
      errors.push('Invalid lobby closure hours format');
      return errors;
    }

    const lunchStartMinutes = timeToMinutes(lunchHours.start);
    const lunchEndMinutes = timeToMinutes(lunchHours.end);

    if (lunchStartMinutes >= lunchEndMinutes) {
      errors.push('Lunch start time must be before lunch end time');
    }

    // Check that lunch hours fit within at least one open day
    const lunchFitsInAnyDay = officeHoursInput.some((day) => {
      if (day.closed || !day.open || !day.close) return false;

      const openMinutes = timeToMinutes(day.open);
      const closeMinutes = timeToMinutes(day.close);

      return (
        openMinutes < lunchStartMinutes &&
        lunchEndMinutes < closeMinutes
      );
    });

    if (!lunchFitsInAnyDay) {
      errors.push(
        'Lobby closure hours must fit within the open hours of at least one day'
      );
    }
  }

  return errors;
};

/**
 * MAIN PIPELINE: The unified, idempotent office hours processing pipeline
 * This is the single function that should be used for all office hours transformations
 * to ensure TC-OH-006 compliance (non-destructive editing)
 */
export const normalizeOfficeHours = (
  apiOfficeHours: OfficeHour[],
  lunchConfig?: LunchHoursConfig | null
): {
  canonical: CanonicalOfficeHours[];
  viewModel: CanonicalOfficeHours[];
  forForm: OfficeHoursInput[];
  lunchExtracted: LunchHoursConfig | null;
} => {
  // Step 1: Convert API data to canonical representation
  const canonical = toCanonical(apiOfficeHours);

  // Step 2: Normalize canonical data (merge overlapping blocks)
  const normalized = normalizeCanonical(canonical);

  // Step 3: Extract lunch hours if not provided
  const extractedLunch =
    lunchConfig || extractLunchHoursCanonical(normalized);

  // Step 4: Apply lunch exclusion to create view model (for UI display)
  const viewModel = applyLunchExclusion(normalized, extractedLunch);

  // Step 5: Convert to form format for UI
  const forForm = fromCanonical(viewModel);

  return {
    canonical: normalized,
    viewModel,
    forForm,
    lunchExtracted: extractedLunch,
  };
};

/**
 * Extract lobby closure hours from canonical format
 */
export const extractLunchHoursCanonical = (
  canonical: CanonicalOfficeHours[]
): LunchHoursConfig | null => {
  // Look for days with exactly 2 blocks (indicating lobby closure hours)
  for (const day of canonical) {
    if (day.blocks.length === 2) {
      const [first, second] = day.blocks.sort(
        (a, b) => a.startMinutes - b.startMinutes
      );

      // Validate that there's a gap between blocks (lunch period)
      if (first.endMinutes < second.startMinutes) {
        return {
          startMinutes: first.endMinutes,
          endMinutes: second.startMinutes,
          enabled: true,
        };
      }
    }
  }

  return null;
};

/**
 * Validate that lunch configuration is compatible with office hours
 */
export const validateLunchCompatibility = (
  canonical: CanonicalOfficeHours[],
  lunchConfig: LunchHoursConfig
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (lunchConfig.startMinutes >= lunchConfig.endMinutes) {
    errors.push('Lunch start time must be before lunch end time');
  }

  // Check that lunch fits within at least one day's office hours
  const lunchFitsInAnyDay = canonical.some((day) => {
    if (day.closed || day.blocks.length === 0) return false;

    return day.blocks.some(
      (block) =>
        block.startMinutes < lunchConfig.startMinutes &&
        lunchConfig.endMinutes < block.endMinutes
    );
  });

  if (!lunchFitsInAnyDay) {
    errors.push(
      'Lunch hours must fit within the open hours of at least one day'
    );
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Create API payload from form input and lunch configuration
 * Uses the canonical pipeline to ensure consistency
 */
export const createAPIPayload = (
  formInput: OfficeHoursInput[],
  lunchConfig: LunchHoursConfig | null
): ProcessedOfficeHours[] => {
  // Convert form input to canonical format
  const canonical = formInput.map((dayInput, index) => {
    const dayOfWeek = index + 1;

    if (dayInput.closed || !dayInput.open || !dayInput.close) {
      return {
        dayOfWeek,
        blocks: [],
        closed: true,
      };
    }

    const startMinutes = timeToMinutes(dayInput.open);
    const endMinutes = timeToMinutes(dayInput.close);

    if (startMinutes >= endMinutes) {
      return {
        dayOfWeek,
        blocks: [],
        closed: true,
      };
    }

    return {
      dayOfWeek,
      blocks: [{ startMinutes, endMinutes }],
      closed: false,
    };
  });

  // Apply lunch exclusion if enabled
  const withLunch = applyLunchExclusion(canonical, lunchConfig);

  // Convert to API format
  const result: ProcessedOfficeHours[] = [];

  withLunch.forEach((day) => {
    if (day.closed || day.blocks.length === 0) {
      return; // Skip closed days
    }

    day.blocks.forEach((block, blockIndex) => {
      result.push({
        dayOfWeek: day.dayOfWeek,
        open_time: minutesToTime(block.startMinutes),
        close_time: minutesToTime(block.endMinutes),
        closed: false,
        endOfDay: blockIndex === day.blocks.length - 1 || undefined,
      });
    });
  });

  return result;
};

/**
 * Validation result for lunch hours compatibility per day
 */
export interface LunchValidationResult {
  valid: boolean;
  errors: string[];
  dayMessages: Array<{
    day: string;
    dayIndex: number;
    message: string;
    applied: boolean;
  }>;
}

/**
 * Copy day hours to other days
 */
export const copyDayToAllDays = (
  officeHoursInput: OfficeHoursInput[],
  sourceDayIndex: number
): OfficeHoursInput[] => {
  const sourceDay = officeHoursInput[sourceDayIndex];
  if (!sourceDay) return officeHoursInput;

  return officeHoursInput.map((day, index) => {
    if (index === sourceDayIndex) return day; // Keep source day unchanged

    return {
      ...day,
      open: sourceDay.open,
      close: sourceDay.close,
      closed: sourceDay.closed,
      openDisplay: sourceDay.openDisplay,
      closeDisplay: sourceDay.closeDisplay,
      openError: false,
      closeError: false,
    };
  });
};

/**
 * Enhanced lunch compatibility validation with per-day feedback
 */
export const validateLunchCompatibilityWithFeedback = (
  canonical: CanonicalOfficeHours[],
  lunchConfig: LunchHoursConfig
): LunchValidationResult => {
  const errors: string[] = [];
  const dayMessages: Array<{
    day: string;
    dayIndex: number;
    message: string;
    applied: boolean;
  }> = [];

  if (lunchConfig.startMinutes >= lunchConfig.endMinutes) {
    errors.push('Lunch start time must be before lunch end time');
  }

  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  // Check each day individually
  canonical.forEach((day) => {
    const dayName = dayNames[day.dayOfWeek - 1];

    if (day.closed || day.blocks.length === 0) {
      dayMessages.push({
        day: dayName,
        dayIndex: day.dayOfWeek - 1,
        message: `${dayName} is closed`,
        applied: false,
      });
      return;
    }

    // Check if lobby closure is completely outside office hours
    // Only reject if lobby closure starts after office closes OR ends before office opens
    const earliestOpen = Math.min(
      ...day.blocks.map((b) => b.startMinutes)
    );
    const latestClose = Math.max(
      ...day.blocks.map((b) => b.endMinutes)
    );

    const lobbyClosureOutsideOfficeHours =
      lunchConfig.startMinutes >= latestClose ||
      lunchConfig.endMinutes <= earliestOpen ||
      lunchConfig.endMinutes > latestClose;

    if (lobbyClosureOutsideOfficeHours) {
      const lunchStartTime = minutesToTime(lunchConfig.startMinutes);
      const lunchEndTime = minutesToTime(lunchConfig.endMinutes);
      const lunchStartDisplay = to12h(lunchStartTime);
      const lunchEndDisplay = to12h(lunchEndTime);
      const closeDisplay = to12h(minutesToTime(latestClose));
      const openDisplay = to12h(minutesToTime(earliestOpen));

      let reason = '';
      if (lunchConfig.startMinutes >= latestClose) {
        reason = `starts after office closes at ${closeDisplay}`;
      } else if (lunchConfig.endMinutes <= earliestOpen) {
        reason = `ends before office opens at ${openDisplay}`;
      } else if (lunchConfig.endMinutes > latestClose) {
        reason = `ends after office closes at ${closeDisplay}`;
      } else {
        reason = `completely outside office hours`;
      }

      dayMessages.push({
        day: dayName,
        dayIndex: day.dayOfWeek - 1,
        message: `Lobby closure ${lunchStartDisplay}–${lunchEndDisplay} cannot be shown on ${dayName} (${reason})`,
        applied: false,
      });
    } else {
      dayMessages.push({
        day: dayName,
        dayIndex: day.dayOfWeek - 1,
        message: `Lunch applied to ${dayName}`,
        applied: true,
      });
    }
  });

  // Check if lunch fits in at least one day
  const lunchFitsInAnyDay = dayMessages.some((msg) => msg.applied);

  if (!lunchFitsInAnyDay) {
    errors.push(
      'Lunch hours must fit within the open hours of at least one day'
    );
  }

  return {
    valid: errors.length === 0,
    errors,
    dayMessages,
  };
};

/**
 * Debug logging for office hours processing
 */
export const debugOfficeHours = (
  label: string,
  _data: unknown,
  lunchConfig?: LunchHoursConfig | null
) => {
  if (import.meta.env.DEV || import.meta.env.VITE_HOURS_DEBUG) {
    console.group(`🕒 Office Hours Debug: ${label}`);
    // Console logging removed for production safety.
    if (lunchConfig) {
      console.log('Lunch Config:', {
        ...lunchConfig,
        startTime: minutesToTime(lunchConfig.startMinutes),
        endTime: minutesToTime(lunchConfig.endMinutes),
      });
    }
    console.groupEnd();
  }
};
